import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { LayerType } from '../types/game';
import { FALLBACK_LAYER_CONFIG } from '../types/layers';
import { gameAPI } from '../lib/gameAPI';

interface AssetLayersContextType {
  layers: LayerType[];
  layerMap: Map<string, LayerType>;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const AssetLayersContext = createContext<AssetLayersContextType | undefined>(undefined);

export const useAssetLayers = () => {
  const context = useContext(AssetLayersContext);
  if (context === undefined) {
    throw new Error('useAssetLayers must be used within an AssetLayersProvider');
  }
  return context;
};

interface AssetLayersProviderProps {
  children: ReactNode;
}

export const AssetLayersProvider: React.FC<AssetLayersProviderProps> = ({ children }) => {
  const [layers, setLayers] = useState<LayerType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const layerMap = useMemo(() => {
    const map = new Map<string, LayerType>();
    layers.forEach(layer => {
      map.set(layer.name, layer);
    });
    return map;
  }, [layers]);

  const loadLayers = async () => {
    try {
      console.log('🎨 [AssetLayers] Loading layers from database...');

      const timeoutPromise = new Promise<LayerType[]>((_, reject) => {
        setTimeout(() => reject(new Error('Loading timeout')), 5000);
      });

      const layersPromise = gameAPI.getAssetLayers();

      const data = await Promise.race([layersPromise, timeoutPromise]);

      if (!data || data.length === 0) {
        console.warn('⚠️ [AssetLayers] No layers returned from API, using fallback');
        setLayers(FALLBACK_LAYER_CONFIG);
        setError('API returned no data');
      } else {
        const sortedLayers = [...data].sort((a, b) => a.layer_order - b.layer_order);
        setLayers(sortedLayers);
        setError(null);
        console.log('✅ [AssetLayers] Loaded', sortedLayers.length, 'layers successfully');
      }
    } catch (err: any) {
      console.error('❌ [AssetLayers] Failed to load layers:', err.message);
      console.warn('⚠️ [AssetLayers] Using fallback configuration');
      setLayers(FALLBACK_LAYER_CONFIG);
      setError(err.message || 'Unknown error');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const refresh = async () => {
    if (isRefreshing) {
      console.log('🔄 [AssetLayers] Refresh already in progress, skipping');
      return;
    }

    setIsRefreshing(true);
    setIsLoading(true);
    await loadLayers();
  };

  useEffect(() => {
    loadLayers();
  }, []);

  const value = {
    layers,
    layerMap,
    isLoading,
    error,
    refresh
  };

  return (
    <AssetLayersContext.Provider value={value}>
      {children}
    </AssetLayersContext.Provider>
  );
};
